import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import "./LiveKitAgent.css";
import "./VisualizerOverrides.css";
import "@livekit/components-styles";
import {
  LiveKitRoom,
  useRoomContext,
  useTracks,
  RoomAudioRenderer,
  useLocalParticipant,
  useVoiceAssistant,
  BarVisualizer,
} from "@livekit/components-react";
import type { LocalUserChoices, TrackReferenceOrPlaceholder } from "@livekit/components-react";
import { Track, RemoteParticipant, Room, LocalAudioTrack, createLocalAudioTrack } from "livekit-client";

// Extracted modules
import type { LiveKitAgentProps, TranscriptMessage, AgentMetrics, MetricsHistoryEntry, InterviewContext, InterviewScores } from "./livekit-types";
import { getTranscriptStorageKey } from "./livekit-types";
import TranscriptMessageComponent from "./TranscriptMessage";
import { useRoomEventHandlers } from "./useRoomEventHandlers";
import { useContextSender } from "./useContextSender";
import MetricsPanel from "./MetricsPanel";
import UnifiedControlBar from "./UnifiedControlBar";
import { VISUALIZER_BAR_COUNT, VISUALIZER_MIN_HEIGHT_PCT, VISUALIZER_MAX_HEIGHT_PCT } from "./visualizerConstants";

// Re-export types for consumers of this module
export type { InterviewContext } from "./livekit-types";

// ─── LiveKitAgentInner ────────────────────────────────────────────────────────
// Runs inside <LiveKitRoom> — manages room events + context sending

const LiveKitAgentInner: React.FC<{
  darkMode?: boolean;
  codeContent?: string;
  compilerOutput?: string;
  language?: string;
  participantCount?: number;
  interviewContext?: InterviewContext | null;
  diagramContent?: string;
  onReconnect?: () => void;
  isConnecting?: boolean;
  onConnectionStateChange?: (connected: boolean) => void;
  connectionAttempt?: number;
  agentRequestedRef?: React.MutableRefObject<boolean>;
  contextSentRef?: React.MutableRefObject<boolean>;
  micEnabled?: boolean;
  setMicEnabled?: (enabled: boolean) => void;
  transcript: TranscriptMessage[];
  setTranscript: React.Dispatch<React.SetStateAction<TranscriptMessage[]>>;
  agentParticipant: RemoteParticipant | null;
  setAgentParticipant: React.Dispatch<React.SetStateAction<RemoteParticipant | null>>;
  receivedMessageIds: React.MutableRefObject<Set<string>>;
  setAgentStatus: React.Dispatch<React.SetStateAction<string>>;
  setAgentActivity: React.Dispatch<React.SetStateAction<string>>;
  setAgentMetrics: React.Dispatch<React.SetStateAction<AgentMetrics>>;
  setMetricsHistory: React.Dispatch<React.SetStateAction<MetricsHistoryEntry[]>>;
  sessionId?: string;
}> = ({
  darkMode,
  codeContent,
  compilerOutput,
  language,
  participantCount,
  interviewContext,
  diagramContent,
  onReconnect,
  isConnecting = false,
  onConnectionStateChange,
  connectionAttempt = 0,
  agentRequestedRef,
  contextSentRef,
  micEnabled = false,
  setMicEnabled = () => { },
  transcript,
  setTranscript,
  agentParticipant,
  setAgentParticipant,
  receivedMessageIds,
  setAgentStatus,
  setAgentActivity,
  setAgentMetrics,
  setMetricsHistory,
  sessionId,
}) => {
    const room = useRoomContext();
    const { localParticipant: localParticipantFromHook } = useLocalParticipant();
    const [error, setError] = useState<string | null>(null);

    const voiceAssistant = useVoiceAssistant();

    const tracks = useTracks(
      [
        { source: Track.Source.Camera, withPlaceholder: true },
        { source: Track.Source.Microphone, withPlaceholder: true },
      ],
      { onlySubscribed: false }
    );

    const localMicTrackRef = useMemo(() => {
      return tracks.find(
        (track) => track.source === Track.Source.Microphone && track.participant.isLocal
      );
    }, [tracks]);

    const visualizerTrackRef = voiceAssistant.audioTrack || localMicTrackRef;

    // Use extracted room event handlers hook
    const { isConnected, remoteParticipants } = useRoomEventHandlers({
      transcript,
      setTranscript,
      agentParticipant,
      setAgentParticipant,
      receivedMessageIds,
      setAgentStatus,
      setAgentActivity,
      setAgentMetrics,
      setMetricsHistory,
      onConnectionStateChange,
      contextSentRef,
      agentRequestedRef,
      onInterviewScores: (scores: InterviewScores) => {
        // Forward scores up — save via data attribute on the container
        const event = new CustomEvent('interviewScoresReceived', { detail: scores });
        window.dispatchEvent(event);
      },
    });

    // Use extracted context sender hook
    useContextSender({
      isConnected,
      agentParticipant,
      codeContent,
      compilerOutput,
      language,
      participantCount,
      interviewContext,
      diagramContent,
      transcript,
      contextSentRef,
    });

    // Save transcript to localStorage whenever it changes
    useEffect(() => {
      if (transcript.length > 0) {
        try {
          const storageKey = getTranscriptStorageKey(sessionId);
          localStorage.setItem(storageKey, JSON.stringify(transcript));
        } catch (err) {
          console.error("Failed to save transcript to localStorage:", err);
        }
      }
    }, [transcript, sessionId]);

    // Handle reconnection errors
    useEffect(() => {
      if (error && onReconnect) {
        const timer = setTimeout(() => {
          onReconnect();
          setError(null);
        }, 5000);
        return () => clearTimeout(timer);
      }
    }, [error, onReconnect]);

    // Not connected yet — parent handles pre-connect UI
    if (!isConnected) {
      return null;
    }

    return (
      <>
        {/* Render room audio - key forces remount on reconnect to reinitialize audio */}
        <RoomAudioRenderer key={`audio-${connectionAttempt}`} />

        <div className="livekit-agent-content active-session">
          {/* Agent Audio Visualizer - shows when agent is speaking */}
          {isConnected && (
            <div className="agent-visualizer-container" style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "1rem",
              minHeight: "120px",
              width: "100%",
              visibility: "visible",
            }}>
              {(() => {
                // Find the agent's audio track from the tracks hook
                const agentTrackRef = tracks.find(
                  (t) =>
                    !t.participant.isLocal &&
                    t.source === Track.Source.Microphone &&
                    t.participant.identity !== localParticipantFromHook?.identity
                );

                // Use voiceAssistant track if available, otherwise fallback to the found agent track
                const trackToUse = voiceAssistant.audioTrack || agentTrackRef;

                return (
                  <BarVisualizer
                    state={voiceAssistant.state}
                    trackRef={trackToUse}
                    barCount={VISUALIZER_BAR_COUNT}
                    options={{
                      minHeight: VISUALIZER_MIN_HEIGHT_PCT,
                      maxHeight: VISUALIZER_MAX_HEIGHT_PCT,
                    }}
                    style={{ width: "100%", height: "100%", backgroundColor: "transparent", visibility: "visible", opacity: 1 }}
                  />
                );
              })()}
            </div>
          )}

          <div className="livekit-agent-controls" title="">
            {/* Controls are handled by UnifiedControlBar in parent */}
          </div>
        </div>
      </>
    );
  };

// ─── Main LiveKitAgent Component ──────────────────────────────────────────────

const LiveKitAgent: React.FC<LiveKitAgentProps> = ({
  sessionId,
  darkMode = false,
  codeContent,
  compilerOutput,
  language,
  participantCount,
  userName,
  interviewContext,
  diagramContent,
}) => {
  // ─── Connection State ─────────────────────────────────────────────────────
  const [token, setToken] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string>("");
  const [participantName, setParticipantName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shouldConnect, setShouldConnect] = useState(false);
  const [userChoices, setUserChoices] = useState<LocalUserChoices | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);

  // ─── Microphone State ─────────────────────────────────────────────────────
  const [permissionError, setPermissionError] = useState<boolean>(false);
  const [permissionStatus, setPermissionStatus] = useState<string>("prompt");
  const [micEnabled, setMicEnabled] = useState(false);
  const [preJoinAudioTrack, setPreJoinAudioTrack] = useState<LocalAudioTrack | null>(null);

  // ─── Refs ─────────────────────────────────────────────────────────────────
  const agentRequestedRef = useRef(false);
  const contextSentRef = useRef(false);
  const prejoinWrapperRef = useRef<HTMLDivElement | null>(null);
  const preconnectContentRef = useRef<HTMLDivElement | null>(null);

  // ─── Agent & Transcript State (hoisted from Inner) ────────────────────────
  const [agentParticipant, setAgentParticipant] = useState<RemoteParticipant | null>(null);
  const [agentStatus, setAgentStatus] = useState<string>("disconnected");
  const [agentActivity, setAgentActivity] = useState<string>("");
  const [agentMetrics, setAgentMetrics] = useState<AgentMetrics>({});
  const [metricsHistory, setMetricsHistory] = useState<MetricsHistoryEntry[]>([]);
  const [showMetrics, setShowMetrics] = useState(false);

  // ─── Interview Scores State ─────────────────────────────────────────────────
  const [interviewScores, setInterviewScores] = useState<InterviewScores | null>(null);
  const sessionStartTimeRef = useRef<number>(Date.now());
  const savedResultRef = useRef<boolean>(false);
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState<number | null>(null);

  const [transcript, setTranscript] = useState<TranscriptMessage[]>(() => {
    try {
      const saved = localStorage.getItem(getTranscriptStorageKey(sessionId));
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((msg: any) => ({ ...msg, timestamp: new Date(msg.timestamp) }));
      }
    } catch (err) {
      console.error("Failed to load transcript from localStorage:", err);
    }
    return [];
  });

  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const transcriptMessagesRef = useRef<HTMLDivElement>(null);
  const receivedMessageIds = useRef<Set<string>>(new Set());
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);

  // ─── Memoized Callbacks (prevent LiveKitRoom re-renders) ──────────────────
  const handleLiveKitConnected = useCallback(() => {
    setIsConnecting(false);
    setConnectionAttempt(0);
  }, []);
  const handleLiveKitDisconnected = useCallback(() => {
    setIsConnecting(false);
    setAgentParticipant(null);
  }, []);
  const handleLiveKitError = useCallback((err: Error) => {
    console.error("LiveKit room error:", err);
    setIsConnecting(false);
    setError(err.message);
  }, []);

  // ─── Listen for Interview Scores from Agent ─────────────────────────────────
  useEffect(() => {
    const handleScores = (e: Event) => {
      const scores = (e as CustomEvent).detail as InterviewScores;
      setInterviewScores(scores);
    };
    window.addEventListener('interviewScoresReceived', handleScores);
    return () => window.removeEventListener('interviewScoresReceived', handleScores);
  }, []);

  // ─── Transcript Export Helpers ──────────────────────────────────────────────
  const exportTranscript = useCallback(() => {
    if (transcript.length === 0) return;
    const lines = transcript.map((msg) => {
      const time = new Date(msg.timestamp).toLocaleTimeString();
      const speaker = msg.sender === 'user' ? 'Candidate' : 'Interviewer';
      return `**${speaker}** (${time}):\n${msg.text}\n`;
    });
    const content = `# Interview Transcript\n\nSession: ${sessionId || 'unknown'}\nDate: ${new Date().toLocaleDateString()}\n\n---\n\n${lines.join('\n')}`;
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${sessionId || 'session'}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [transcript, sessionId]);

  const exportSummary = useCallback(async () => {
    if (transcript.length === 0) return;
    const baseURL =
      process.env.NODE_ENV === 'production' &&
        (!process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL.includes('localhost'))
        ? 'https://interview-sandbox-production.up.railway.app'
        : process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    try {
      const transcriptData = transcript.map((msg) => ({
        sender: msg.sender,
        text: msg.text,
        timestamp: new Date(msg.timestamp).toISOString(),
      }));
      const response = await fetch(`${baseURL}/api/candidate-responses/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript: transcriptData }),
      });
      if (!response.ok) throw new Error('Failed to generate summary');
      const data = await response.json();
      const content = `# Interview Summary\n\nSession: ${sessionId || 'unknown'}\nDate: ${new Date().toLocaleDateString()}\n\n---\n\n${data.summary}`;
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `summary-${sessionId || 'session'}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export summary:', err);
    }
  }, [transcript, sessionId]);

  // ─── Auto-save Candidate Response on Disconnect ────────────────────────────
  const saveSessionResult = useCallback(async () => {
    if (savedResultRef.current || transcript.length === 0) return;
    savedResultRef.current = true;

    const baseURL =
      process.env.NODE_ENV === 'production' &&
        (!process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL.includes('localhost'))
        ? 'https://interview-sandbox-production.up.railway.app'
        : process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

    // For interviews, sessionId is the interviewId (from URL /box/:id)
    const interviewId = interviewContext ? (sessionId || 'unknown') : null;
    if (!interviewId) return;

    const timeSpent = Math.round((Date.now() - sessionStartTimeRef.current) / 60000);
    const transcriptData = transcript.map((msg) => ({
      sender: msg.sender,
      text: msg.text,
      timestamp: new Date(msg.timestamp).toISOString(),
    }));

    try {
      await fetch(`${baseURL}/api/candidate-responses/${encodeURIComponent(interviewId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId || `session-${Date.now()}`,
          name: interviewContext?.candidateName || userName || 'Anonymous',
          email: '',
          code: codeContent || '',
          language: language || 'Unknown',
          timeSpent,
          transcript: transcriptData,
          scores: interviewScores,
          summary: interviewScores?.summary || '',
          status: 'completed',
          completedAt: new Date().toISOString(),
        }),
      });
      console.log('✅ Candidate response saved');
    } catch (err) {
      console.error('Failed to save candidate response:', err);
    }
  }, [transcript, interviewContext, sessionId, userName, codeContent, language, interviewScores]);

  // ─── Interview Countdown Timer ─────────────────────────────────────────────
  const saveSessionResultRef = useRef(saveSessionResult);
  saveSessionResultRef.current = saveSessionResult;
  const timeLimitMinutes = interviewContext?.timeLimit ?? 60;
  useEffect(() => {
    if (!interviewContext || !shouldConnect) {
      setTimeRemainingSeconds(null);
      return;
    }
    const totalSeconds = timeLimitMinutes * 60;
    const updateRemaining = () => {
      const elapsed = (Date.now() - sessionStartTimeRef.current) / 1000;
      const remaining = Math.max(0, Math.floor(totalSeconds - elapsed));
      setTimeRemainingSeconds(remaining);
      if (remaining <= 0) {
        saveSessionResultRef.current();
        setShouldConnect(false);
      }
    };
    updateRemaining();
    const interval = setInterval(updateRemaining, 1000);
    return () => clearInterval(interval);
  }, [interviewContext, shouldConnect, timeLimitMinutes]);

  const liveKitRoomOptions = useMemo(
    () => ({
      publishDefaults: {
        audioPreset: { maxBitrate: 32000 },
        dtx: true,
      },
      audioCaptureDefaults: {
        autoGainControl: true,
        echoCancellation: true,
        ...(userChoices?.audioDeviceId && { deviceId: userChoices.audioDeviceId }),
      },
    }),
    [userChoices?.audioDeviceId]
  );

  // ─── Microphone Permission ────────────────────────────────────────────────
  useEffect(() => {
    const checkPermission = async () => {
      try {
        if (navigator.permissions) {
          const result = await navigator.permissions.query({ name: "microphone" as PermissionName });
          setPermissionStatus(result.state);
          setPermissionError(result.state === "denied");
          result.onchange = () => {
            setPermissionStatus(result.state);
            setPermissionError(result.state === "denied");
          };
        }
      } catch (err) {
        console.log("Could not check microphone permission:", err);
      }
    };
    checkPermission();
  }, []);

  const requestMicrophonePermission = async () => {
    try {
      if (preJoinAudioTrack) return true;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setPermissionError(false);
      setPermissionStatus("granted");
      return true;
    } catch (err: any) {
      console.error("❌ Microphone permission denied:", err);
      setPermissionError(true);
      setPermissionStatus("denied");
      return false;
    }
  };

  // ─── Pre-join Audio Track Management ──────────────────────────────────────
  // Use a ref to always have access to the current track without adding it
  // to the dependency array (which would cause re-creation loops)
  const preJoinAudioTrackRef = useRef<LocalAudioTrack | null>(null);
  preJoinAudioTrackRef.current = preJoinAudioTrack;

  useEffect(() => {
    let track: LocalAudioTrack | null = null;
    const manageTrack = async () => {
      if (micEnabled && !preJoinAudioTrackRef.current) {
        try {
          track = await createLocalAudioTrack({
            echoCancellation: true,
            noiseSuppression: true,
          });
          setPreJoinAudioTrack(track);
          setPermissionError(false);
          setPermissionStatus("granted");
        } catch (err) {
          console.error("❌ Failed to create local audio track:", err);
          setMicEnabled(false);
          setPermissionError(true);
          setPermissionStatus("denied");
        }
      } else if (!micEnabled && preJoinAudioTrackRef.current) {
        preJoinAudioTrackRef.current.stop();
        setPreJoinAudioTrack(null);
      }
    };
    manageTrack();
    return () => {
      // Stop track created in this effect run on cleanup
      if (track) track.stop();
      // Also stop current track ref on unmount
      if (preJoinAudioTrackRef.current) preJoinAudioTrackRef.current.stop();
    };
  }, [micEnabled]);

  // ─── Display Name ─────────────────────────────────────────────────────────
  const displayName = useMemo(() => {
    if (userName) return userName;
    try {
      const storedName = localStorage.getItem("anonymousUserName");
      if (storedName) return storedName;
    } catch (e) { /* ignore */ }
    return "User";
  }, [userName]);

  // ─── Status Helpers ───────────────────────────────────────────────────────
  const getStatusMessage = (status: string) => {
    switch (status) {
      case "disconnected": return "Disconnected";
      case "waiting_for_agent": return "Waiting for agent...";
      case "agent_joined": return "Agent starting...";
      case "agent_ready": return "Agent ready";
      case "active": return "Active";
      default: return status;
    }
  };

  // ─── Prevent Form Submit in PreJoin ───────────────────────────────────────
  useEffect(() => {
    const cleanupFunctions: Array<() => void> = [];
    const preventFormSubmit = (el: HTMLElement | null) => {
      if (el) {
        const form = el.querySelector("form");
        if (form) {
          const handleSubmit = (e: Event) => { e.preventDefault(); e.stopPropagation(); return false; };
          form.addEventListener("submit", handleSubmit);
          cleanupFunctions.push(() => form.removeEventListener("submit", handleSubmit));
        }
      }
    };
    const timeoutId = setTimeout(() => {
      preventFormSubmit(prejoinWrapperRef.current);
      preventFormSubmit(preconnectContentRef.current);
    }, 100);
    return () => { clearTimeout(timeoutId); cleanupFunctions.forEach((c) => c()); };
  }, [shouldConnect]);

  // ─── Token Fetching ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchToken = async () => {
      if (!sessionId) {
        console.error("❌ No sessionId provided — cannot create stable room name");
        setError("Session ID required for agent connection");
        return;
      }

      const generatedRoomName = `room-${sessionId}`;
      const generatedParticipantName = `user-${displayName}-${Date.now()}`;

      const baseURL =
        process.env.NODE_ENV === "production" &&
          (!process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL.includes("localhost"))
          ? "https://interview-sandbox-production.up.railway.app"
          : process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

      try {
        setLoading(true);
        setError(null);
        setAgentParticipant(null);

        const response = await fetch(`${baseURL}/api/livekit/token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            roomName: generatedRoomName,
            participantName: generatedParticipantName,
            agentName: "Alex-12d6",
          }),
        });

        if (!response.ok) {
          let errorMessage = `HTTP error! status: ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch { errorMessage = response.statusText || errorMessage; }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        setToken(data.token);
        setUrl(data.url);
        setRoomName(data.roomName);
        setParticipantName(data.participantName);

        // Request agent to join the room (fire immediately — no delay needed)
        if (!agentRequestedRef.current) {
          agentRequestedRef.current = true;
          (async () => {
            try {
              const agentResponse = await fetch(`${baseURL}/api/livekit/request-agent`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ roomName: data.roomName, agentName: "Alex-12d6" }),
              });
              if (agentResponse.ok) {
                await agentResponse.json();
              } else {
                const errorText = await agentResponse.text();
                console.warn("⚠️ Agent dispatch failed (may already exist):", agentResponse.status, errorText);
              }
            } catch (agentErr) {
              console.warn("⚠️ Could not dispatch agent (may already exist):", agentErr);
            }
          })();
        }
      } catch (err) {
        console.error("Error fetching LiveKit token:", err);
        setError(err instanceof Error ? err.message : "Failed to connect to agent");
      } finally {
        setLoading(false);
      }
    };

    fetchToken();
  }, [sessionId, refreshToken]);

  // ─── Connection Attempt ───────────────────────────────────────────────────
  useEffect(() => {
    if (connectionAttempt > 0 && userChoices) {
      setIsConnecting(true);
      setShouldConnect(true);
    }
  }, [connectionAttempt]);

  useEffect(() => {
    if (!shouldConnect) setIsConnecting(false);
  }, [shouldConnect]);

  // ─── Auto-scroll Transcript ───────────────────────────────────────────────
  // Use scrollTo on the transcript container instead of scrollIntoView to avoid
  // scrolling the viewport (which would push the agent header and diagram toolbar out of view)
  useEffect(() => {
    const container = transcriptMessagesRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, [transcript]);

  useEffect(() => {
    const messagesContainer = transcriptMessagesRef.current;
    if (!messagesContainer) return;
    const handleScroll = () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      setIsScrolling(true);
      scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 2000);
    };
    messagesContainer.addEventListener("scroll", handleScroll);
    return () => {
      messagesContainer.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  // ─── Agent Container Ref (for MetricsPanel anchor) ────────────────────────
  const agentContainerRef = useRef<HTMLDivElement>(null);
  const [, setContainerMounted] = useState(false);
  const setAgentContainerRef = useCallback((el: HTMLDivElement | null) => {
    agentContainerRef.current = el;
    setContainerMounted((prev) => prev || Boolean(el));
  }, []);

  // ─── Header Status ────────────────────────────────────────────────────────
  const getHeaderStatus = () => {
    if (shouldConnect && !isConnecting && agentParticipant) return "connected";
    if (shouldConnect || loading) return "connecting";
    return "disconnected";
  };

  const getConnectionStatusText = () => {
    if (agentActivity) return agentActivity;
    if (isConnecting) return "Connecting...";
    if (shouldConnect && !isConnecting && agentParticipant) return getStatusMessage(agentStatus);
    if (shouldConnect) return "Connecting...";
    if (!shouldConnect && transcript.length > 0) return "Disconnected";
    return "Not Connected";
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div ref={setAgentContainerRef} className={`livekit-agent-container ${darkMode ? "dark" : ""}`}>
      {/* Persistent Header */}
      <div className="livekit-agent-header">
        <div className="livekit-agent-status">
          <span className={`status-indicator ${getHeaderStatus()}`}></span>
          <span>{getConnectionStatusText()}</span>
        </div>
        {timeRemainingSeconds != null && timeRemainingSeconds > 0 && (
          <div className="livekit-agent-countdown" title="Time remaining">
            {Math.floor(timeRemainingSeconds / 60)}:{(timeRemainingSeconds % 60).toString().padStart(2, '0')}
          </div>
        )}
        {transcript.length > 0 && (
          <>
            <button
              onClick={() => {
                setTranscript([]);
                receivedMessageIds.current.clear();
                localStorage.removeItem(getTranscriptStorageKey(sessionId));
              }}
              className="clear-transcript-button"
              title="Clear conversation history"
            >
              Clear
            </button>
            <button
              onClick={exportTranscript}
              className="clear-transcript-button"
              title="Download transcript as Markdown"
              style={{ marginLeft: '0.25rem' }}
            >
              Export
            </button>
            <button
              onClick={exportSummary}
              className="clear-transcript-button"
              title="Generate and download AI summary"
              style={{ marginLeft: '0.25rem' }}
            >
              Summary
            </button>
          </>
        )}
      </div>

      {/* Persistent Content Wrapper */}
      <div className="livekit-agent-content">
        {/* Transcript Section — Always Visible */}
        <div className="livekit-transcript-container">
          <div
            ref={transcriptMessagesRef}
            className={`livekit-transcript-messages ${isScrolling ? "scrolling" : ""}`}
          >
            {transcript.length === 0 ? (
              <div className="transcript-empty">
                <p>Start speaking to see the transcript here...</p>
              </div>
            ) : (
              transcript.map((message) => (
                <TranscriptMessageComponent
                  key={message.id}
                  message={message}
                  darkMode={darkMode}
                />
              ))
            )}
            <div ref={transcriptEndRef} />
          </div>
        </div>

        {/* Dynamic Bottom Section */}
        <div className="livekit-dynamic-content" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "220px", width: "100%", overflow: "hidden" }}>
          {loading ? (
            <div className={`livekit-agent-loading ${darkMode ? "dark" : ""}`}>
              <div className="spinner"></div>
              <p>Initializing connection...</p>
            </div>
          ) : error ? (
            <div className={`livekit-agent-error ${darkMode ? "dark" : ""}`}>
              <p>Error: {error}</p>
              <p className="error-hint">Please ensure LiveKit credentials are configured on the server.</p>
            </div>
          ) : !token || !url ? (
            <div className={`livekit-agent-error ${darkMode ? "dark" : ""}`}>
              <p>Failed to obtain connection credentials</p>
            </div>
          ) : (
            <LiveKitRoom
              token={token}
              serverUrl={url}
              connect={shouldConnect}
              audio={micEnabled}
              video={false}
              onConnected={handleLiveKitConnected}
              onDisconnected={handleLiveKitDisconnected}
              onError={handleLiveKitError}
              options={liveKitRoomOptions}
              className={`livekit-room-provider ${darkMode ? "dark" : ""}`}
              style={{ height: "100%", display: "flex", flex: 1 }}
            >
              {!shouldConnect ? (
                <div className="prejoin-wrapper">
                  <div className="livekit-prejoin-content" style={{
                    display: "flex", flexDirection: "column", gap: "1.5rem", alignItems: "center", padding: "2rem",
                  }}>
                    <div title=""></div>
                  </div>
                </div>
              ) : (
                <LiveKitAgentInner
                  darkMode={darkMode}
                  codeContent={codeContent}
                  compilerOutput={compilerOutput}
                  language={language}
                  participantCount={participantCount}
                  interviewContext={interviewContext}
                  diagramContent={diagramContent}
                  isConnecting={isConnecting}
                  connectionAttempt={connectionAttempt}
                  agentRequestedRef={agentRequestedRef}
                  contextSentRef={contextSentRef}
                  micEnabled={micEnabled}
                  setMicEnabled={setMicEnabled}
                  sessionId={sessionId}
                  onReconnect={() => {
                    setIsConnecting(true);
                    setShouldConnect(true);
                    setConnectionAttempt((prev) => prev + 1);
                    agentRequestedRef.current = false;
                    setRefreshToken((prev) => prev + 1);
                  }}
                  transcript={transcript}
                  setTranscript={setTranscript}
                  agentParticipant={agentParticipant}
                  setAgentParticipant={setAgentParticipant}
                  receivedMessageIds={receivedMessageIds}
                  setAgentStatus={setAgentStatus}
                  setAgentActivity={setAgentActivity}
                  setAgentMetrics={setAgentMetrics}
                  setMetricsHistory={setMetricsHistory}
                  onConnectionStateChange={(connected) => {
                    if (connected) {
                      setIsConnecting(false);
                    } else {
                      // Fired from handleDisconnected — room actually disconnected
                      setIsConnecting(false);
                      setShouldConnect(false);
                      // Auto-save candidate response on disconnect
                      saveSessionResult();
                    }
                  }}
                />
              )}
              <UnifiedControlBar
                micEnabled={micEnabled}
                setMicEnabled={setMicEnabled}
                preJoinAudioTrack={preJoinAudioTrack}
                shouldConnect={shouldConnect}
                transcriptLength={transcript.length}
                displayName={displayName}
                setUserChoices={setUserChoices}
                setConnectionAttempt={setConnectionAttempt}
                agentRequestedRef={agentRequestedRef}
                setRefreshToken={setRefreshToken}
                roomName={roomName}
                permissionError={permissionError}
                onCheckPermissions={requestMicrophonePermission}
                permissionStatus={permissionStatus}
              />

              <MetricsPanel
                darkMode={darkMode}
                agentMetrics={agentMetrics}
                metricsHistory={metricsHistory}
                showMetrics={showMetrics}
                setShowMetrics={setShowMetrics}
                containerRef={agentContainerRef}
              />
            </LiveKitRoom>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveKitAgent;
